import React from 'react';
import { flexRender } from '@tanstack/react-table';
import styles from './TableBody.module.css';

export function TableBody({
  virtualRows,
  virtualColumns,
  tableRows,
  stickyColumns: _stickyColumns,
  scrollableColumns,
  paddingTop,
  paddingBottom,
  paddingLeft,
  paddingRight,
  focusedCell,
  handleCellClick,
  columnIndexMap,
}) {
  return (
    // eslint-disable-next-line jsx-a11y/no-redundant-roles
    <tbody role="rowgroup">
      {paddingTop > 0 && (
        <tr>
          <td style={{ height: paddingTop }} />
        </tr>
      )}
      {virtualRows.map((virtualRow) => {
        const row = tableRows[virtualRow.index];
        const isOddRow = virtualRow.index % 2 === 1;
        const rowClassName = `${styles.row} ${isOddRow ? styles.oddRow : styles.evenRow}`;
        // Row index for data-grid-id (offset by 3 for header rows: category=0, assignment=1, average=2)
        const gridRowIndex = virtualRow.index + 3;

        return (
          <tr
            key={row.id}
            className={rowClassName}
            style={{
              height: `${virtualRow.size}px`,
            }}
            role="row"
            aria-rowindex={virtualRow.index + 4}
          >
            {/* Render sticky name column */}
            {row
              .getVisibleCells()
              .filter((cell) => cell.column.id === 'name')
              .map((cell) => {
                const isFocused =
                  focusedCell?.rowIndex === virtualRow.index &&
                  focusedCell?.columnId === cell.column.id;

                const cellClasses = [
                  styles.cell,
                  styles.stickyCell,
                  isOddRow ? styles.stickyCellOdd : styles.stickyCellEven,
                  isFocused ? styles.focusedCell : '',
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <th
                    key={cell.id}
                    className={cellClasses}
                    style={{
                      position: 'sticky',
                      left: 0,
                      width: '250px',
                      minWidth: '250px',
                      maxWidth: '250px',
                    }}
                    role="rowheader"
                    scope="row"
                    tabIndex={isFocused ? 0 : -1}
                    aria-colindex={
                      (columnIndexMap?.get(cell.column.id) ?? 0) + 1
                    }
                    data-row-index={virtualRow.index}
                    data-column-id={cell.column.id}
                    data-grid-id={`r${gridRowIndex}-c${columnIndexMap?.get(cell.column.id) ?? 0}`}
                    aria-label={`Student: ${row.original.student.name}`}
                    onClick={() =>
                      handleCellClick(virtualRow.index, cell.column.id)
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </th>
                );
              })}
            {/* Padding for horizontal scroll - always render for stable layout */}
            <td
              style={{
                width: paddingLeft,
                minWidth: paddingLeft,
                maxWidth: paddingLeft,
                padding: 0,
                border: 'none',
              }}
            />
            {/* Render virtual columns */}
            {virtualColumns.map((virtualColumn) => {
              const column = scrollableColumns[virtualColumn.index];
              const cell = row
                .getVisibleCells()
                .find((c) => c.column.id === column.id);

              if (!cell) return <td key={`empty-${virtualColumn.index}`} />;

              const meta = cell.column.columnDef.meta;
              const isFocused =
                focusedCell?.rowIndex === virtualRow.index &&
                focusedCell?.columnId === cell.column.id;

              const cellClasses = [
                styles.cell,
                isOddRow ? styles.oddCell : styles.evenCell,
                meta?.isCategoryTotal ? styles.categoryTotalCell : '',
                cell.column.id === 'courseTotal' ? styles.courseTotalCell : '',
                isFocused ? styles.focusedCell : '',
              ]
                .filter(Boolean)
                .join(' ');

              return (
                // eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role
                <td
                  key={cell.id}
                  className={cellClasses}
                  style={{
                    width: `${virtualColumn.size}px`,
                    minWidth: `${virtualColumn.size}px`,
                    maxWidth: `${virtualColumn.size}px`,
                  }}
                  role="gridcell"
                  tabIndex={isFocused ? 0 : -1}
                  aria-colindex={(columnIndexMap?.get(cell.column.id) ?? 0) + 1}
                  data-row-index={virtualRow.index}
                  data-column-id={cell.column.id}
                  data-grid-id={`r${gridRowIndex}-c${columnIndexMap?.get(cell.column.id) ?? 0}`}
                  onClick={() =>
                    handleCellClick(virtualRow.index, cell.column.id)
                  }
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              );
            })}
            {/* Padding for horizontal scroll (right) - for columns after visible ones */}
            <td
              style={{
                width: paddingRight,
                minWidth: paddingRight,
                maxWidth: paddingRight,
                padding: 0,
                border: 'none',
              }}
            />
          </tr>
        );
      })}
      {paddingBottom > 0 && (
        <tr>
          <td style={{ height: paddingBottom }} />
        </tr>
      )}
    </tbody>
  );
}
